import React, { useState } from 'react';
import moment from 'moment';
import { Button, Input, DatePicker, Tooltip, notification } from 'antd';
import styled from 'styled-components';
import BackendService from 'services/BackendService';
import DeviceTypeTreeSelector from 'containers/DeviceTypeTreeSelector';

const { RangePicker } = DatePicker;
const iso8601format = 'YYYY-MM-DDTHH:mm:ss';

const TIMEFRAME = {
	DAY: 'DAY',
	WEEK: 'WEEK',
	MONTH: 'MONTH',
	ALL_TIME: 'ALL_TIME',
	CUSTOM: 'CUSTOM',
};

const ToolbarContainer = styled.div`
	margin-bottom: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const Row = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 12px;

	.ant-form-item {
		margin-bottom: 0;
	}
`;

const TimestampLabelGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
`;

const TimeframeButtonGroup = styled.div`
	display: flex;
	gap: 4px;
`;

const TimeframeButton = styled(Button)`
	${(props) => props.active && `
		background-color: #595959;
		color: #fff;
		border-color: #595959;
	`}
`;

const getTimeframeRange = (timeframe) => {
	const to = moment();
	switch (timeframe) {
		case TIMEFRAME.DAY:      return [moment().subtract(1, 'days'), to];
		case TIMEFRAME.WEEK:     return [moment().subtract(1, 'weeks'), to];
		case TIMEFRAME.MONTH:    return [moment().subtract(1, 'months'), to];
		case TIMEFRAME.ALL_TIME: return [moment('2000-01-01').startOf('day'), to];
		default:                 return [null, null];
	}
};

const EventsGridToolbar = (props) => {
	const { setGridParams, grid, refreshGrid, deviceTypesTreeData, deviceTypesIdToLabelMap } = props;

	const getDeviceTypeName = (id) => {
		if (!id || !deviceTypesIdToLabelMap) return id;
		const fullPath = deviceTypesIdToLabelMap[id];
		if (!fullPath) return id;
		return fullPath.split('/').pop();
	};

	const [timeframe, setTimeframe] = useState(TIMEFRAME.DAY);
	const [dateRange, setDateRange] = useState(getTimeframeRange(TIMEFRAME.DAY));
	const [deviceSerialNumber, setDeviceSerialNumber] = useState('');
	const [deviceType, setDeviceType] = useState(undefined);
	const [entryKey, setEntryKey] = useState('');
	const [searchText, setSearchText] = useState('');
	const [exportLoading, setExportLoading] = useState(false);

	const buildFilters = ({ dsn, dt, ek, from, to } = {}) => {
		const filters = {};
		const resolvedDsn    = dsn  !== undefined ? dsn  : deviceSerialNumber;
		const resolvedDt     = dt   !== undefined ? dt   : deviceType;
		const resolvedEk     = ek   !== undefined ? ek   : entryKey;
		const [resolvedFrom, resolvedTo] = from !== undefined ? [from, to] : dateRange;

		if (resolvedFrom) filters.entryTimestampAfter  = resolvedFrom.format(iso8601format);
		if (resolvedTo)   filters.entryTimestampBefore = resolvedTo.format(iso8601format);
		if (resolvedDsn)  filters.deviceSerialNumber   = resolvedDsn;
		if (resolvedDt)   filters.deviceType           = getDeviceTypeName(resolvedDt);
		if (resolvedEk)   filters.entryKey             = resolvedEk;
		return filters;
	};

	const applyFilters = (overrides = {}) => {
		setGridParams({
			...grid.params,
			pagination: { current: 1, pageSize: grid.params.pagination.pageSize || 10 },
			filters: buildFilters(overrides),
			search: overrides.search !== undefined ? overrides.search : searchText,
		});
	};

	const handleDeviceTypeChange = (value) => {
		setDeviceType(value);
	};

	const handleGetData = () => {
		applyFilters({ dt: deviceType, dsn: deviceSerialNumber });
	};

	// Refresh must reflect the toolbar's current state, not the last-applied Redux
	// params. Sync Redux (so subsequent pagination/sort use the current filters) and
	// force a reload with those same filters.
	const handleRefresh = () => {
		const filters = buildFilters({ dt: deviceType, dsn: deviceSerialNumber });
		const pagination = { current: 1, pageSize: grid.params.pagination.pageSize || 10 };
		const nextParams = { ...grid.params, pagination, filters, search: searchText };
		setGridParams(nextParams);
		refreshGrid(nextParams);
	};

	const handleTimeframeClick = (tf) => {
		setTimeframe(tf);
		const range = getTimeframeRange(tf);
		setDateRange(range);
	};

	const handleCustomRangeChange = (range) => {
		setTimeframe(TIMEFRAME.CUSTOM);
		setDateRange(range || [null, null]);
	};

	const handleReset = () => {
		const defaultRange = getTimeframeRange(TIMEFRAME.DAY);
		setTimeframe(TIMEFRAME.DAY);
		setDateRange(defaultRange);
		setDeviceSerialNumber('');
		setDeviceType(undefined);
		setEntryKey('');
		setSearchText('');
		setGridParams({
			...grid.params,
			pagination: { current: 1, pageSize: grid.params.pagination.pageSize || 10 },
			filters: {
				entryTimestampAfter:  defaultRange[0].format(iso8601format),
				entryTimestampBefore: defaultRange[1].format(iso8601format),
			},
			search: '',
		});
	};

	const handleExport = async () => {
		if (!deviceType || !deviceSerialNumber) return;
		const [from, to] = dateRange;
		setExportLoading(true);
		try {
			await BackendService.exportCsv({
				exportType: 'EVENTS',
				deviceType: getDeviceTypeName(deviceType),
				deviceSerialNumber,
				fromDate: from ? from.toISOString() : undefined,
				toDate: to ? to.toISOString() : undefined,
			});
			notification.success({
				message: 'Export started',
				description: 'You will receive an email with a download link when the export is ready.',
			});
		} catch (err) {
			notification.error({ message: 'Export failed', description: 'Please try again.' });
		} finally {
			setExportLoading(false);
		}
	};

	const canQuery = !!(deviceType && deviceSerialNumber);

	return (
		<ToolbarContainer>
		<Row>
			<TimestampLabelGroup>
				<span style={{ color: '#595959', fontSize: 12, fontWeight: 500 }}>Creation Time:</span>
				<TimeframeButtonGroup>
					{[TIMEFRAME.DAY, TIMEFRAME.WEEK, TIMEFRAME.MONTH, TIMEFRAME.ALL_TIME, TIMEFRAME.CUSTOM].map((tf) => (
						<TimeframeButton
							key={tf}
							active={timeframe === tf ? 1 : 0}
							onClick={() => tf !== TIMEFRAME.CUSTOM ? handleTimeframeClick(tf) : setTimeframe(TIMEFRAME.CUSTOM)}
						>
							{tf === TIMEFRAME.ALL_TIME ? 'All time' : tf.charAt(0) + tf.slice(1).toLowerCase()}
						</TimeframeButton>
					))}
				</TimeframeButtonGroup>
			</TimestampLabelGroup>

			{timeframe === TIMEFRAME.CUSTOM && (
				<RangePicker
					showTime
					value={dateRange[0] && dateRange[1] ? [dateRange[0], dateRange[1]] : undefined}
					onChange={handleCustomRangeChange}
					placeholder={['From', 'To']}
				/>
			)}

			{timeframe !== TIMEFRAME.CUSTOM && (
				<span style={{ color: '#595959', fontSize: 12 }}>
					{dateRange[0]?.format('YYYY-MM-DD HH:mm')} → {dateRange[1]?.format('YYYY-MM-DD HH:mm')}
				</span>
			)}
		</Row>

		<Row>
				<DeviceTypeTreeSelector
					value={deviceType}
					onChange={handleDeviceTypeChange}
					multiSelect={false}
					allowClear
					placeholder="Device Type"
					style={{ width: 280 }}
				/>
				<Input
					placeholder="Device Serial Number"
					value={deviceSerialNumber}
					onChange={(e) => setDeviceSerialNumber(e.target.value)}
					allowClear
					style={{ width: 220 }}
				/>
				<Input
					placeholder="Entry Key"
					value={entryKey}
					onChange={(e) => setEntryKey(e.target.value)}
					allowClear
					style={{ width: 200 }}
				/>
				<Input
					placeholder="Search"
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					allowClear
					style={{ width: 180 }}
				/>
				<Tooltip title={canQuery ? 'Fetch events data' : 'Device Type and Serial Number are required'}>
					<Button
						type="primary"
						icon="search"
						disabled={!canQuery}
						onClick={handleGetData}
					>
						Get Data
					</Button>
				</Tooltip>
				<Button onClick={handleReset}>Reset</Button>
				<Tooltip title={canQuery ? 'Export events to CSV (email notification)' : 'Device Type and Serial Number are required for export'}>
					<Button
						type="primary"
						icon="download"
						loading={exportLoading}
						disabled={!canQuery}
						onClick={handleExport}
					>
						Export
					</Button>
				</Tooltip>
				<Button
					type="primary"
					shape="circle"
					icon="reload"
					disabled={!canQuery}
					onClick={handleRefresh}
					style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginLeft: 'auto' }}
				/>
			</Row>
		</ToolbarContainer>
	);
};

export default EventsGridToolbar;
